import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as d3 from 'd3';

const MARGIN = { top: 20, right: 20, bottom: 50, left: 60 };
const VIEW_WIDTH = 700;
const VIEW_HEIGHT = 400;

const styles = {
  card: {
    position: 'relative',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '4px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '12px',
  },
  summary: {
    fontSize: '13px',
    color: '#374151',
    marginBottom: '12px',
    fontWeight: 500,
  },
  outlierBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px 10px',
    borderRadius: '999px',
    background: '#fef2f2',
    border: '1px solid #fecaca',
    color: '#dc2626',
    fontSize: '12px',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  filterBar: {
    display: 'flex',
    gap: '8px',
    marginBottom: '12px',
  },
  filterButton: {
    padding: '4px 12px',
    borderRadius: '6px',
    border: '1px solid #e5e7eb',
    background: '#fff',
    color: '#374151',
    fontSize: '12px',
    fontWeight: 500,
    cursor: 'pointer',
  },
  filterButtonActive: {
    padding: '4px 12px',
    borderRadius: '6px',
    border: '1px solid #2563eb',
    background: '#eff6ff',
    color: '#2563eb',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    marginBottom: '12px',
    alignItems: 'center',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
    fontWeight: 500,
    color: '#374151',
  },
  legendDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  container: {
    overflowX: 'auto',
    minWidth: '400px',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    maxWidth: '260px',
  },
  emptyState: {
    color: '#6b7280',
    textAlign: 'center',
    padding: '40px 0',
  },
};

export default function AddressOutlierMap({ data }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);
  const [filter, setFilter] = useState('all');

  const filteredAddresses = useMemo(() => {
    if (!data || !data.addresses) return [];
    if (filter === 'all') return data.addresses;
    if (filter === 'outliers') return data.addresses.filter((a) => a.isOutlier);
    if (filter === 'critical') {
      const critThreshold = data.thresholds ? data.thresholds.critical : 3;
      return data.addresses.filter((a) => Math.abs(a.zScore) > critThreshold);
    }
    return data.addresses;
  }, [data, filter]);

  useEffect(() => {
    if (!filteredAddresses || filteredAddresses.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const innerW = VIEW_WIDTH - MARGIN.left - MARGIN.right;
    const innerH = VIEW_HEIGHT - MARGIN.top - MARGIN.bottom;

    svg
      .attr('viewBox', `0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`)
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .style('width', '100%')
      .style('height', 'auto');

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    const warningThreshold = data.thresholds ? data.thresholds.warning : 2;
    const criticalThreshold = data.thresholds ? data.thresholds.critical : 3;

    // Scales
    const xExtent = d3.extent(filteredAddresses, (d) => d.stateMean);
    const yExtent = d3.extent(filteredAddresses, (d) => d.price);
    const allExtent = [
      Math.min(xExtent[0], yExtent[0]),
      Math.max(xExtent[1], yExtent[1]),
    ];
    const pad = (allExtent[1] - allExtent[0]) * 0.1 || 5;

    const x = d3.scaleLinear()
      .domain([allExtent[0] - pad, allExtent[1] + pad])
      .nice()
      .range([0, innerW]);

    const y = d3.scaleLinear()
      .domain([allExtent[0] - pad, allExtent[1] + pad])
      .nice()
      .range([innerH, 0]);

    // Grid lines - horizontal
    g.selectAll('.grid-h')
      .data(y.ticks())
      .enter()
      .append('line')
      .attr('class', 'grid-h')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', (d) => y(d))
      .attr('y2', (d) => y(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // Grid lines - vertical
    g.selectAll('.grid-v')
      .data(x.ticks())
      .enter()
      .append('line')
      .attr('class', 'grid-v')
      .attr('x1', (d) => x(d))
      .attr('x2', (d) => x(d))
      .attr('y1', 0)
      .attr('y2', innerH)
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // Diagonal reference line (y = x)
    const domainMin = Math.max(x.domain()[0], y.domain()[0]);
    const domainMax = Math.min(x.domain()[1], y.domain()[1]);
    g.append('line')
      .attr('x1', x(domainMin))
      .attr('y1', y(domainMin))
      .attr('x2', x(domainMax))
      .attr('y2', y(domainMax))
      .attr('stroke', '#d1d5db')
      .attr('stroke-width', 1.5)
      .attr('stroke-dasharray', '6,4');

    // Label for diagonal
    g.append('text')
      .attr('x', x(domainMax) - 4)
      .attr('y', y(domainMax) + 14)
      .attr('text-anchor', 'end')
      .attr('font-size', '10px')
      .attr('fill', '#9ca3af')
      .attr('font-style', 'italic')
      .text('price = state mean');

    // State mean markers on x-axis
    const stateMeans = [...new Set(filteredAddresses.map((a) => a.stateMean))];
    stateMeans.forEach((mean) => {
      g.append('line')
        .attr('x1', x(mean))
        .attr('x2', x(mean))
        .attr('y1', innerH)
        .attr('y2', innerH + 6)
        .attr('stroke', '#9ca3af')
        .attr('stroke-width', 1.5);
    });

    // X axis
    g.append('g')
      .attr('transform', `translate(0, ${innerH})`)
      .call(
        d3.axisBottom(x)
          .ticks(8)
          .tickSize(0)
          .tickFormat((d) => `$${d}`)
      )
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    // X axis label
    g.append('text')
      .attr('x', innerW / 2)
      .attr('y', innerH + 40)
      .attr('text-anchor', 'middle')
      .attr('font-size', '11px')
      .attr('fill', '#6b7280')
      .text('State Mean Price ($)');

    // Y axis
    g.append('g')
      .call(
        d3.axisLeft(y)
          .ticks(8)
          .tickSize(0)
          .tickFormat((d) => `$${d}`)
      )
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    // Y axis label
    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -innerH / 2)
      .attr('y', -45)
      .attr('text-anchor', 'middle')
      .attr('font-size', '11px')
      .attr('fill', '#6b7280')
      .text('Address Price ($)');

    // Size scale based on |deviationPercent|
    const devExtent = d3.extent(filteredAddresses, (d) => Math.abs(d.deviationPercent));
    const sizeScale = d3.scaleLinear()
      .domain([0, devExtent[1] || 1])
      .range([4, 12])
      .clamp(true);

    // Helper to determine point color
    function pointColor(addr) {
      if (!addr.isOutlier) return '#6b7280';
      if (Math.abs(addr.zScore) > criticalThreshold) return '#dc2626';
      return '#f59e0b';
    }

    // Draw normal points first, then warning, then critical (so critical is on top)
    const normal = filteredAddresses.filter((a) => !a.isOutlier);
    const warning = filteredAddresses.filter(
      (a) => a.isOutlier && Math.abs(a.zScore) <= criticalThreshold
    );
    const critical = filteredAddresses.filter(
      (a) => a.isOutlier && Math.abs(a.zScore) > criticalThreshold
    );

    const drawPoints = (points) => {
      points.forEach((addr) => {
        const r = Math.max(4, sizeScale(Math.abs(addr.deviationPercent)));
        const color = pointColor(addr);
        const circle = g.append('circle')
          .attr('cx', x(addr.stateMean))
          .attr('cy', y(addr.price))
          .attr('r', r)
          .attr('fill', color)
          .attr('opacity', addr.isOutlier ? 0.9 : 0.6)
          .attr('stroke', addr.isOutlier ? '#fff' : 'none')
          .attr('stroke-width', addr.isOutlier ? 1.5 : 0)
          .style('cursor', 'pointer');

        circle
          .on('mousemove', (event) => {
            d3.select(event.currentTarget).attr('r', r + 3);
            setTooltip({
              x: event.clientX + 12,
              y: event.clientY - 10,
              item: addr,
              color,
            });
          })
          .on('mouseleave', (event) => {
            d3.select(event.currentTarget).attr('r', r);
            setTooltip(null);
          });
      });
    };

    drawPoints(normal);
    drawPoints(warning);
    drawPoints(critical);

    return () => {
      svg.selectAll('*').remove();
    };
  }, [filteredAddresses, data]);

  if (!data || !data.addresses || data.addresses.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>Address-Level Price Outliers</div>
        <div style={styles.subtitle}>
          Individual address prices vs their state average -- points off the diagonal are outliers
        </div>
        <div style={styles.emptyState}>No data available</div>
      </div>
    );
  }

  const outlierCount = data.outlierCount != null
    ? data.outlierCount
    : data.addresses.filter((a) => a.isOutlier).length;
  const totalCount = data.totalCount != null
    ? data.totalCount
    : data.addresses.length;

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div>
          <div style={styles.title}>Address-Level Price Outliers</div>
        </div>
        {outlierCount > 0 && (
          <div style={styles.outlierBadge}>
            {outlierCount} outlier{outlierCount !== 1 ? 's' : ''}
          </div>
        )}
      </div>
      <div style={styles.subtitle}>
        Individual address prices vs their state average -- points off the diagonal are outliers
      </div>
      <div style={styles.summary}>
        {outlierCount} outlier{outlierCount !== 1 ? 's' : ''} detected out of {totalCount} address{totalCount !== 1 ? 'es' : ''}
      </div>
      <div style={styles.filterBar}>
        {['all', 'outliers', 'critical'].map((f) => {
          const label = f === 'all' ? 'All' : f === 'outliers' ? 'Outliers Only' : 'Critical Only';
          return (
            <button
              key={f}
              style={filter === f ? styles.filterButtonActive : styles.filterButton}
              onClick={() => setFilter(f)}
            >
              {label}
            </button>
          );
        })}
      </div>
      <div style={styles.legend}>
        <div style={styles.legendItem}>
          <span style={{ ...styles.legendDot, background: '#6b7280' }} />
          <span>Normal</span>
        </div>
        <div style={styles.legendItem}>
          <span style={{ ...styles.legendDot, background: '#f59e0b' }} />
          <span>Warning</span>
        </div>
        <div style={styles.legendItem}>
          <span style={{ ...styles.legendDot, background: '#dc2626' }} />
          <span>Critical</span>
        </div>
        <div style={styles.legendItem}>
          <span
            style={{
              width: '20px',
              height: '0px',
              borderTop: '1.5px dashed #d1d5db',
              flexShrink: 0,
            }}
          />
          <span>y = x (state mean)</span>
        </div>
      </div>
      <div style={styles.container} ref={containerRef}>
        <svg ref={svgRef} />
      </div>
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {tooltip.item.label}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>State</span>
            <span style={{ fontWeight: 600 }}>{tooltip.item.state}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Price</span>
            <span style={{ fontWeight: 600 }}>${tooltip.item.price.toFixed(2)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>State Mean</span>
            <span style={{ fontWeight: 600 }}>${tooltip.item.stateMean.toFixed(2)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Deviation ($)</span>
            <span style={{ fontWeight: 600, color: tooltip.item.deviation >= 0 ? '#dc2626' : '#16a34a' }}>
              {tooltip.item.deviation >= 0 ? '+' : ''}{tooltip.item.deviation.toFixed(2)}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Deviation (%)</span>
            <span style={{ fontWeight: 600, color: tooltip.item.deviationPercent >= 0 ? '#dc2626' : '#16a34a' }}>
              {tooltip.item.deviationPercent >= 0 ? '+' : ''}{tooltip.item.deviationPercent.toFixed(1)}%
            </span>
          </div>
          {tooltip.item.zScore != null && (
            <div style={{ marginTop: '4px', paddingTop: '4px', borderTop: '1px solid #f3f4f6' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
                <span style={{ color: '#6b7280' }}>Z-Score</span>
                <span style={{ fontWeight: 600, color: tooltip.color }}>
                  {tooltip.item.zScore >= 0 ? '+' : ''}{tooltip.item.zScore.toFixed(2)}
                </span>
              </div>
              {tooltip.item.isOutlier && (
                <div style={{ fontSize: '11px', fontWeight: 600, color: tooltip.color, marginTop: '4px' }}>
                  {Math.abs(tooltip.item.zScore) > (data.thresholds ? data.thresholds.critical : 3)
                    ? 'Critical Outlier'
                    : 'Warning Outlier'}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
